/**
 * Financial Settings Tab - Consolidated financial settings
 *
 * Phase 8.3 - Consolidates invoice and proposal settings into logical hierarchy:
 * - Tax Settings (shared by invoices & proposals)
 * - Invoice Settings (payment terms, notes, etc.)
 * - Proposal Settings (payment terms[], validity, tax display)
 *
 * Tax settings are stored in `invoice_defaults` and used by both invoices and proposals.
 * The backend PDF generator fetches tax_label from invoice_defaults.
 */
import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useInvoiceDefaults, useUpdateInvoiceDefaults, useProposalDefaults, useUpdateProposalDefaults, useFinancialSettings, useUpdateFinancialSettings, useTaxSettings, useUpdateTaxSettings } from '@/hooks';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { DollarSign, FileText, FileSignature, Save, Plus, Trash2, ChevronDown, ChevronRight, Target, Bell } from 'lucide-react';
import { CollectionRemindersSettings } from '@/components/settings/CollectionRemindersSettings';
import type { InvoiceDefaults, ProposalDefaults, TaxDisplayMode, FinancialSettings, TaxSettings } from '@/lib/schemas';

// Default payment terms from Jamie's actual proposals
const DEFAULT_PROPOSAL_PAYMENT_TERMS = [
  "Accounts will be submitted monthly, based on percentage complete, and are due when rendered. Work is assumed to be 100% complete upon acceptance of deliverables listed in the fee schedule.",
  "Late payments are subject to an interest charge of 2% per month (24.00% per annum).",
  "Payments can be made via cheque, e-transfer, or direct deposit."
];

// Invoice payment terms options
const INVOICE_PAYMENT_TERMS_OPTIONS = [
  { value: 0, label: 'Due on Receipt' },
  { value: 15, label: 'Net 15' },
  { value: 30, label: 'Net 30' },
  { value: 45, label: 'Net 45' },
  { value: 60, label: 'Net 60' },
  { value: 90, label: 'Net 90' },
];

// Tax display mode options for proposal PDFs
const TAX_DISPLAY_MODE_OPTIONS: { value: TaxDisplayMode; label: string; description: string }[] = [
  { value: 'none', label: 'No Tax', description: 'Just show the subtotal (e.g., "$11,000")' },
  { value: 'label', label: 'Tax Label Only', description: 'Show tax indicator (e.g., "$11,000 + GST")' },
  { value: 'calculated', label: 'Calculated Tax', description: 'Show calculated tax amount (e.g., "$11,000 + $550 GST")' },
];

// Section identifiers for URL routing
type SectionId = 'goals' | 'tax' | 'invoices' | 'proposals' | 'collection-reminders';

interface CollapsibleSectionProps {
  id: SectionId;
  title: string;
  description: string;
  icon: React.ReactNode;
  isExpanded: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}

function CollapsibleSection({ id, title, description, icon, isExpanded, onToggle, children }: CollapsibleSectionProps) {
  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden" data-section={id}>
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center gap-3 p-4 bg-gray-50 hover:bg-gray-100 transition-colors text-left"
        aria-expanded={isExpanded}
      >
        {isExpanded ? (
          <ChevronDown className="w-5 h-5 text-gray-500 flex-shrink-0" />
        ) : (
          <ChevronRight className="w-5 h-5 text-gray-500 flex-shrink-0" />
        )}
        <div className="text-slate-600 flex-shrink-0">{icon}</div>
        <div className="flex-1 min-w-0">
          <h3 className="text-base font-semibold text-gray-900">{title}</h3>
          <p className="text-sm text-gray-600 mt-0.5">{description}</p>
        </div>
      </button>
      {isExpanded && (
        <div className="p-4 border-t border-gray-200 bg-white">
          {children}
        </div>
      )}
    </div>
  );
}

export function FinancialSettingsTab() {
  const [searchParams, setSearchParams] = useSearchParams();

  // Get section from URL (default: all collapsed - QF-03)
  const urlSection = searchParams.get('section') as SectionId | null;

  // Track which sections are expanded (default collapsed for easier initial navigation)
  const [expandedSections, setExpandedSections] = useState<Record<SectionId, boolean>>({
    goals: false,
    tax: false,
    invoices: false,
    proposals: false,
    'collection-reminders': false,
  });

  // If URL has section param, only expand that section
  useEffect(() => {
    if (urlSection && ['goals', 'tax', 'invoices', 'proposals', 'collection-reminders'].includes(urlSection)) {
      setExpandedSections({
        goals: urlSection === 'goals',
        tax: urlSection === 'tax',
        invoices: urlSection === 'invoices',
        proposals: urlSection === 'proposals',
        'collection-reminders': urlSection === 'collection-reminders',
      });
      // Scroll to section after content renders
      setTimeout(() => {
        const element = document.querySelector(`[data-section="${urlSection}"]`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
          // Flash highlight effect to draw attention
          element.classList.add('ring-2', 'ring-purple-400', 'ring-offset-2');
          setTimeout(() => {
            element.classList.remove('ring-2', 'ring-purple-400', 'ring-offset-2');
          }, 2000);
        }
      }, 200);
    }
  }, [urlSection]);

  const toggleSection = (section: SectionId) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section],
    }));
    // Update URL to remove section param when manually toggling
    if (urlSection) {
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('section');
      setSearchParams(newParams);
    }
  };

  // Financial settings (revenue goals)
  const { data: financialData, isLoading: financialLoading } = useFinancialSettings();
  const updateFinancialSettings = useUpdateFinancialSettings();

  // Invoice defaults (includes tax settings)
  const { data: invoiceData, isLoading: invoiceLoading } = useInvoiceDefaults();
  const updateInvoiceDefaults = useUpdateInvoiceDefaults();

  // Proposal defaults
  const { data: proposalData, isLoading: proposalLoading } = useProposalDefaults();
  const updateProposalDefaults = useUpdateProposalDefaults();

  // Tax settings (GST remittance + corp tax widgets)
  const { data: taxData, isLoading: taxLoading } = useTaxSettings();
  const updateTaxSettings = useUpdateTaxSettings();

  // Form state for financial settings (revenue goals + fiscal year)
  const [financialFormData, setFinancialFormData] = useState<Partial<FinancialSettings>>({
    annual_revenue_goal: 0,
    fiscal_year_start_month: 1,
  });

  // Form state for invoice/tax settings
  const [invoiceFormData, setInvoiceFormData] = useState<Partial<InvoiceDefaults>>({
    payment_terms: 30,
    tax_rate: 5.0,
    tax_label: 'GST',
    default_notes: '',
    terms_conditions: '',
    payment_instructions: '',
  });

  // Form state for proposal settings
  const [proposalFormData, setProposalFormData] = useState<Partial<ProposalDefaults>>({
    tax_display_mode: 'calculated',
    payment_terms: DEFAULT_PROPOSAL_PAYMENT_TERMS,
    validity_days: 90,
    document_prefix: '',  // 9.9c: Custom prefix for Fee Letter
  });

  // Form state for tax remittance settings
  const [taxFormData, setTaxFormData] = useState<Partial<TaxSettings>>({
    gst_remittance_period: 'quarterly',
    gst_reminder_days: 14,
    corp_tax_rate: 0,
    corp_tax_remittance_dates: [],
  });

  // Track if forms have unsaved changes
  const [financialHasChanges, setFinancialHasChanges] = useState(false);
  const [invoiceHasChanges, setInvoiceHasChanges] = useState(false);
  const [proposalHasChanges, setProposalHasChanges] = useState(false);
  const [taxHasChanges, setTaxHasChanges] = useState(false);

  // Initialize financial form with fetched data
  useEffect(() => {
    if (financialData) {
      setFinancialFormData(financialData);
      setFinancialHasChanges(false);
    }
  }, [financialData]);

  // Initialize invoice form with fetched data
  useEffect(() => {
    if (invoiceData) {
      setInvoiceFormData(invoiceData);
      setInvoiceHasChanges(false);
    }
  }, [invoiceData]);

  // Initialize proposal form with fetched data
  useEffect(() => {
    if (proposalData) {
      setProposalFormData(proposalData);
      setProposalHasChanges(false);
    }
  }, [proposalData]);

  // Initialize tax form with fetched data
  useEffect(() => {
    if (taxData) {
      setTaxFormData(taxData);
      setTaxHasChanges(false);
    }
  }, [taxData]);

  const handleFinancialChange = (field: keyof FinancialSettings, value: number) => {
    setFinancialFormData(prev => ({
      ...prev,
      [field]: value,
    }));
    setFinancialHasChanges(true);
  };

  const handleInvoiceChange = (field: keyof InvoiceDefaults, value: string | number) => {
    setInvoiceFormData(prev => ({
      ...prev,
      [field]: value,
    }));
    setInvoiceHasChanges(true);
  };

  const handleProposalChange = (field: keyof ProposalDefaults, value: string | number | string[]) => {
    setProposalFormData(prev => ({
      ...prev,
      [field]: value,
    }));
    setProposalHasChanges(true);
  };

  const handleProposalPaymentTermChange = (index: number, value: string) => {
    const newTerms = [...(proposalFormData.payment_terms || [])];
    newTerms[index] = value;
    handleProposalChange('payment_terms', newTerms);
  };

  const handleAddProposalPaymentTerm = () => {
    handleProposalChange('payment_terms', [...(proposalFormData.payment_terms || []), '']);
  };

  const handleRemoveProposalPaymentTerm = (index: number) => {
    const newTerms = (proposalFormData.payment_terms || []).filter((_, i) => i !== index);
    handleProposalChange('payment_terms', newTerms);
  };

  const handleSaveFinancial = async () => {
    await updateFinancialSettings.mutateAsync(financialFormData as FinancialSettings);
    setFinancialHasChanges(false);
  };

  const handleSaveTaxAndInvoice = async () => {
    await updateInvoiceDefaults.mutateAsync(invoiceFormData);
    setInvoiceHasChanges(false);
  };

  const handleTaxChange = (field: keyof TaxSettings, value: string | number | string[]) => {
    setTaxFormData(prev => ({ ...prev, [field]: value }));
    setTaxHasChanges(true);
  };

  const handleAddRemittanceDate = () => {
    handleTaxChange('corp_tax_remittance_dates', [...(taxFormData.corp_tax_remittance_dates || []), '']);
  };

  const handleRemoveRemittanceDate = (index: number) => {
    handleTaxChange('corp_tax_remittance_dates', (taxFormData.corp_tax_remittance_dates || []).filter((_, i) => i !== index));
  };

  const handleRemittanceDateChange = (index: number, value: string) => {
    const dates = [...(taxFormData.corp_tax_remittance_dates || [])];
    dates[index] = value;
    handleTaxChange('corp_tax_remittance_dates', dates);
  };

  const handleSaveTaxSettings = async () => {
    await updateTaxSettings.mutateAsync(taxFormData);
    setTaxHasChanges(false);
  };

  const handleSaveProposal = async () => {
    await updateProposalDefaults.mutateAsync(proposalFormData as ProposalDefaults);
    setProposalHasChanges(false);
  };

  const isLoading = financialLoading || invoiceLoading || proposalLoading || taxLoading;

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-20 w-full" />
        <Skeleton className="h-20 w-full" />
        <Skeleton className="h-20 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center gap-3 pb-4 border-b">
        <DollarSign className="w-5 h-5 text-slate-600" />
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Financial Settings</h2>
          <p className="text-sm text-gray-600 mt-1">
            Configure tax rates, invoice defaults, and proposal settings
          </p>
        </div>
      </div>

      {/* Revenue Goals Section */}
      <CollapsibleSection
        id="goals"
        title="Revenue Goals"
        description="Track progress toward annual revenue targets"
        icon={<Target className="w-5 h-5" />}
        isExpanded={expandedSections.goals}
        onToggle={() => toggleSection('goals')}
      >
        <div className="space-y-4">
          {/* Annual Revenue Goal */}
          <div>
            <label htmlFor="annual-revenue-goal" className="block text-sm font-medium text-gray-700 mb-2">
              Annual Revenue Goal
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
              <input
                id="annual-revenue-goal"
                name="annual_revenue_goal"
                type="number"
                value={financialFormData.annual_revenue_goal ?? 0}
                onChange={(e) => handleFinancialChange('annual_revenue_goal', parseFloat(e.target.value) || 0)}
                min="0"
                step="1000"
                className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                placeholder="500000"
              />
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Your target revenue for the year. Set to 0 to hide the goal widget on the dashboard.
            </p>
          </div>

          {/* Fiscal Year Start Month */}
          <div>
            <label htmlFor="fiscal-year-start" className="block text-sm font-medium text-gray-700 mb-2">
              Fiscal Year Start
            </label>
            <select
              id="fiscal-year-start"
              name="fiscal_year_start_month"
              value={financialFormData.fiscal_year_start_month ?? 1}
              onChange={(e) => handleFinancialChange('fiscal_year_start_month', parseInt(e.target.value))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            >
              <option value={1}>January</option>
              <option value={2}>February</option>
              <option value={3}>March</option>
              <option value={4}>April</option>
              <option value={5}>May</option>
              <option value={6}>June</option>
              <option value={7}>July</option>
              <option value={8}>August</option>
              <option value={9}>September</option>
              <option value={10}>October</option>
              <option value={11}>November</option>
              <option value={12}>December</option>
            </select>
            <p className="text-xs text-gray-500 mt-1">
              Month when your fiscal year begins. Revenue tracking and goal progress will align to this period.
            </p>
          </div>

          {/* Info about the dashboard widget */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm text-blue-800">
              When a goal is set, a progress widget will appear on your dashboard showing:
            </p>
            <ul className="text-sm text-sky-700 mt-2 ml-4 list-disc">
              <li>Year-to-date revenue based on your fiscal year</li>
              <li>Progress toward your annual goal</li>
              <li>Whether you&apos;re ahead or behind pace for the year</li>
            </ul>
          </div>
        </div>

        {/* Save button for financial settings */}
        <div className="flex justify-end mt-4 pt-4 border-t border-gray-100">
          <Button
            type="button"
            onClick={handleSaveFinancial}
            disabled={updateFinancialSettings.isPending}
            size="sm"
            className="inline-flex items-center gap-2"
          >
            <Save className="w-4 h-4" />
            {updateFinancialSettings.isPending ? 'Saving...' : financialHasChanges ? 'Save Goal' : 'Saved'}
          </Button>
        </div>
      </CollapsibleSection>

      {/* Tax Settings Section */}
      <CollapsibleSection
        id="tax"
        title="Tax Settings"
        description="Shared tax configuration for invoices and proposals"
        icon={<DollarSign className="w-5 h-5" />}
        isExpanded={expandedSections.tax}
        onToggle={() => toggleSection('tax')}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Tax Rate */}
          <div>
            <label htmlFor="tax-rate" className="block text-sm font-medium text-gray-700 mb-2">
              Tax Rate (%) <span className="text-red-500">*</span>
            </label>
            <input
              id="tax-rate"
              name="tax_rate"
              type="number"
              value={invoiceFormData.tax_rate ?? 5.0}
              onChange={(e) => handleInvoiceChange('tax_rate', parseFloat(e.target.value))}
              required
              min="0"
              max="100"
              step="0.01"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              placeholder="5.0"
            />
            <p className="text-xs text-gray-500 mt-1">
              Default tax percentage (e.g., 5.0 for 5% GST)
            </p>
          </div>

          {/* Tax Label */}
          <div>
            <label htmlFor="tax-label" className="block text-sm font-medium text-gray-700 mb-2">
              Tax Label <span className="text-red-500">*</span>
            </label>
            <input
              id="tax-label"
              name="tax_label"
              type="text"
              value={invoiceFormData.tax_label || ''}
              onChange={(e) => handleInvoiceChange('tax_label', e.target.value)}
              required
              maxLength={50}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              placeholder="GST, HST, PST, etc."
            />
            <p className="text-xs text-gray-500 mt-1">
              Label displayed on invoices and proposals (e.g., "GST", "HST")
            </p>
          </div>
        </div>

        {/* Tax Remittance & Corp Tax sub-block */}
        <div className="mt-6 pt-6 border-t border-gray-100">
          <h4 className="text-sm font-semibold text-gray-800 mb-4">Remittance &amp; Corporate Tax</h4>
          <p className="text-xs text-gray-500 mb-4">
            Configure these fields to enable the GST Remittance and Corporate Tax widgets on your dashboard.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* GST Remittance Period */}
            <div>
              <label htmlFor="gst-remittance-period" className="block text-sm font-medium text-gray-700 mb-2">
                GST Remittance Period
              </label>
              <select
                id="gst-remittance-period"
                value={taxFormData.gst_remittance_period || 'quarterly'}
                onChange={(e) => handleTaxChange('gst_remittance_period', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              >
                <option value="quarterly">Quarterly</option>
                <option value="annual">Annual</option>
              </select>
              <p className="text-xs text-gray-500 mt-1">How often you remit GST to the CRA</p>
            </div>

            {/* GST Reminder Days */}
            <div>
              <label htmlFor="gst-reminder-days" className="block text-sm font-medium text-gray-700 mb-2">
                GST Reminder Lead Time (Days)
              </label>
              <input
                id="gst-reminder-days"
                type="number"
                min={1}
                max={90}
                value={taxFormData.gst_reminder_days ?? 14}
                onChange={(e) => handleTaxChange('gst_reminder_days', parseInt(e.target.value) || 14)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
              <p className="text-xs text-gray-500 mt-1">Days before deadline to highlight the GST widget</p>
            </div>

            {/* Corp Tax Rate */}
            <div>
              <label htmlFor="corp-tax-rate" className="block text-sm font-medium text-gray-700 mb-2">
                Corporate Tax Rate (%)
              </label>
              <input
                id="corp-tax-rate"
                type="number"
                min={0}
                max={100}
                step={0.01}
                value={taxFormData.corp_tax_rate ?? 0}
                onChange={(e) => handleTaxChange('corp_tax_rate', parseFloat(e.target.value) || 0)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                placeholder="15.0"
              />
              <p className="text-xs text-gray-500 mt-1">e.g., 15 for 15% federal corp tax (Canada)</p>
            </div>
          </div>

          {/* Corp Tax Remittance Dates */}
          <div className="mt-4">
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-700">
                Corp Tax Remittance Dates
              </label>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleAddRemittanceDate}
                className="inline-flex items-center gap-1 text-xs"
              >
                <Plus className="w-3 h-3" />
                Add Date
              </Button>
            </div>
            <p className="text-xs text-gray-500 mb-3">
              Annual installment dates in MM-DD format (e.g., <code>03-31</code>, <code>06-15</code>)
            </p>
            {(taxFormData.corp_tax_remittance_dates || []).length === 0 && (
              <p className="text-sm text-gray-400 italic">No dates configured — add dates to enable the corp tax widget.</p>
            )}
            <div className="space-y-2">
              {(taxFormData.corp_tax_remittance_dates || []).map((date, index) => (
                <div key={index} className="flex gap-2 items-center">
                  <input
                    type="text"
                    value={date}
                    onChange={(e) => handleRemittanceDateChange(index, e.target.value)}
                    placeholder="MM-DD (e.g. 03-31)"
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => handleRemoveRemittanceDate(index)}
                    className="text-red-500 hover:text-red-700 hover:bg-red-50 px-2"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Save button for tax settings (invoice label/rate) */}
        <div className="flex justify-end gap-2 mt-4 pt-4 border-t border-gray-100">
          <Button
            type="button"
            onClick={handleSaveTaxSettings}
            disabled={updateTaxSettings.isPending}
            size="sm"
            variant="outline"
            className="inline-flex items-center gap-2"
          >
            <Save className="w-4 h-4" />
            {updateTaxSettings.isPending ? 'Saving...' : taxHasChanges ? 'Save Remittance Settings' : 'Saved'}
          </Button>
          <Button
            type="button"
            onClick={handleSaveTaxAndInvoice}
            disabled={updateInvoiceDefaults.isPending || !invoiceFormData.tax_label}
            size="sm"
            className="inline-flex items-center gap-2"
          >
            <Save className="w-4 h-4" />
            {updateInvoiceDefaults.isPending ? 'Saving...' : invoiceHasChanges ? 'Save Tax Settings' : 'Saved'}
          </Button>
        </div>
      </CollapsibleSection>

      {/* Invoice Settings Section */}
      <CollapsibleSection
        id="invoices"
        title="Invoice Settings"
        description="Default values for new invoices"
        icon={<FileText className="w-5 h-5" />}
        isExpanded={expandedSections.invoices}
        onToggle={() => toggleSection('invoices')}
      >
        <div className="space-y-6">
          {/* Payment Terms */}
          <div>
            <label htmlFor="invoice-payment-terms" className="block text-sm font-medium text-gray-700 mb-2">
              Payment Terms <span className="text-red-500">*</span>
            </label>
            <select
              id="invoice-payment-terms"
              name="payment_terms"
              value={invoiceFormData.payment_terms || 30}
              onChange={(e) => handleInvoiceChange('payment_terms', parseInt(e.target.value))}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            >
              {INVOICE_PAYMENT_TERMS_OPTIONS.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <p className="text-xs text-gray-500 mt-1">
              Number of days until payment is due
            </p>
          </div>

          {/* Default Notes */}
          <div>
            <label htmlFor="default-notes" className="block text-sm font-medium text-gray-700 mb-2">
              Default Invoice Notes
            </label>
            <textarea
              id="default-notes"
              name="default_notes"
              value={invoiceFormData.default_notes || ''}
              onChange={(e) => handleInvoiceChange('default_notes', e.target.value)}
              rows={3}
              maxLength={2000}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              placeholder="Thank you for your business. Please remit payment within the specified terms."
            />
            <p className="text-xs text-gray-500 mt-1">
              These notes will appear on all new invoices (optional)
            </p>
          </div>

          {/* Terms and Conditions */}
          <div>
            <label htmlFor="terms-conditions" className="block text-sm font-medium text-gray-700 mb-2">
              Terms and Conditions
            </label>
            <textarea
              id="terms-conditions"
              name="terms_conditions"
              value={invoiceFormData.terms_conditions || ''}
              onChange={(e) => handleInvoiceChange('terms_conditions', e.target.value)}
              rows={3}
              maxLength={2000}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              placeholder="Payment is due upon receipt of invoice. Late payments may be subject to interest charges."
            />
            <p className="text-xs text-gray-500 mt-1">
              Legal terms and conditions for invoices (optional)
            </p>
          </div>

          {/* Payment Instructions -- R4 (SPEC-tenant-identity-settings-2026-08-17):
              the sole place bank/EFT details render on an invoice now. The
              five structured Canadian bank fields (bank name, account name,
              bank/transit/account number) and the invoice's own unconditional
              e-transfer line are both retired; this free text is what prints
              in their place. */}
          <div>
            <label htmlFor="payment-instructions" className="block text-sm font-medium text-gray-700 mb-2">
              Payment Instructions
            </label>
            <textarea
              id="payment-instructions"
              name="payment_instructions"
              value={invoiceFormData.payment_instructions || ''}
              onChange={(e) => handleInvoiceChange('payment_instructions', e.target.value)}
              rows={4}
              maxLength={2000}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              placeholder={"EFT payments:\nBank: Royal Bank of Canada\nBank Account Name: Your Company Inc\nBank Number: 0003\nTransit Number: 01094\nAccount Number: 1234567"}
            />
            <p className="text-xs text-gray-500 mt-1">
              This is the ONE place bank/EFT/wire details go -- there are no separate bank fields anymore. Whatever you type here (including line breaks) prints on every invoice, exactly as written (optional).
            </p>
          </div>
        </div>

        {/* Save button for invoice settings */}
        <div className="flex justify-end mt-4 pt-4 border-t border-gray-100">
          <Button
            type="button"
            onClick={handleSaveTaxAndInvoice}
            disabled={updateInvoiceDefaults.isPending || !invoiceFormData.tax_label}
            size="sm"
            className="inline-flex items-center gap-2"
          >
            <Save className="w-4 h-4" />
            {updateInvoiceDefaults.isPending ? 'Saving...' : invoiceHasChanges ? 'Save Invoice Settings' : 'Saved'}
          </Button>
        </div>
      </CollapsibleSection>

      {/* Proposal Settings Section */}
      <CollapsibleSection
        id="proposals"
        title="Proposal Settings"
        description="Default values for proposal PDFs (Fee Letters)"
        icon={<FileSignature className="w-5 h-5" />}
        isExpanded={expandedSections.proposals}
        onToggle={() => toggleSection('proposals')}
      >
        <div className="space-y-6">
          {/* Tax Display and Validity Days - side by side */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Tax Display Mode */}
            <div>
              <label htmlFor="tax-display-mode" className="block text-sm font-medium text-gray-700 mb-2">
                Tax Display on Totals <span className="text-red-500">*</span>
              </label>
              <select
                id="tax-display-mode"
                name="tax_display_mode"
                value={proposalFormData.tax_display_mode || 'calculated'}
                onChange={(e) => handleProposalChange('tax_display_mode', e.target.value as TaxDisplayMode)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              >
                {TAX_DISPLAY_MODE_OPTIONS.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <p className="text-xs text-gray-500 mt-1">
                {TAX_DISPLAY_MODE_OPTIONS.find(o => o.value === (proposalFormData.tax_display_mode || 'calculated'))?.description}
              </p>
            </div>

            {/* Validity Days */}
            <div>
              <label htmlFor="validity-days" className="block text-sm font-medium text-gray-700 mb-2">
                Proposal Validity (Days) <span className="text-red-500">*</span>
              </label>
              <input
                id="validity-days"
                name="validity_days"
                type="number"
                value={proposalFormData.validity_days ?? 90}
                onChange={(e) => handleProposalChange('validity_days', parseInt(e.target.value) || 90)}
                required
                min="1"
                max="365"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
              <p className="text-xs text-gray-500 mt-1">
                How long proposals are valid (shown at bottom of PDF)
              </p>
            </div>
          </div>

          {/* Document Prefix (9.9c) */}
          <div>
            <label htmlFor="document-prefix" className="block text-sm font-medium text-gray-700 mb-2">
              Fee Letter Prefix
            </label>
            <input
              id="document-prefix"
              name="document_prefix"
              type="text"
              value={proposalFormData.document_prefix || ''}
              onChange={(e) => handleProposalChange('document_prefix', e.target.value)}
              maxLength={50}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              placeholder="e.g., Banks"
            />
            <p className="text-xs text-gray-500 mt-1">
              Custom prefix for the document title (e.g., "Banks" → "Banks Fee Letter").
              Leave empty to use your company name.
            </p>
          </div>

          {/* Payment Terms */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-700">
                Payment Terms <span className="text-red-500">*</span>
              </label>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleAddProposalPaymentTerm}
                className="inline-flex items-center gap-1 text-xs"
              >
                <Plus className="w-3 h-3" />
                Add Term
              </Button>
            </div>
            <p className="text-xs text-gray-500 mb-3">
              These bullet points appear in the "Payment" section of proposal PDFs
            </p>

            <div className="space-y-3">
              {(proposalFormData.payment_terms || []).map((term, index) => (
                <div key={index} className="flex gap-2">
                  <textarea
                    value={term}
                    onChange={(e) => handleProposalPaymentTermChange(index, e.target.value)}
                    rows={2}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                    placeholder={`Payment term ${index + 1}...`}
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => handleRemoveProposalPaymentTerm(index)}
                    className="text-red-500 hover:text-red-700 hover:bg-red-50 px-2"
                    disabled={(proposalFormData.payment_terms || []).length <= 1}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Save button for proposal settings */}
        <div className="flex justify-end mt-4 pt-4 border-t border-gray-100">
          <Button
            type="button"
            onClick={handleSaveProposal}
            disabled={updateProposalDefaults.isPending || (proposalFormData.payment_terms || []).some(t => !t.trim())}
            size="sm"
            className="inline-flex items-center gap-2"
          >
            <Save className="w-4 h-4" />
            {updateProposalDefaults.isPending ? 'Saving...' : proposalHasChanges ? 'Save Proposal Settings' : 'Saved'}
          </Button>
        </div>
      </CollapsibleSection>

      {/* Collection Reminder Emails (R4) */}
      <CollapsibleSection
        id="collection-reminders"
        title="Collection Reminder Emails"
        description="Daily email listing collection follow-ups due today — independent subscribe path"
        icon={<Bell className="w-5 h-5" />}
        isExpanded={expandedSections['collection-reminders']}
        onToggle={() => toggleSection('collection-reminders')}
      >
        <CollectionRemindersSettings />
      </CollapsibleSection>
    </div>
  );
}
